import { combineEpics, ofType } from "redux-observable"
import {
    map,
    flatMap,
    filter,
    catchError,
    ignoreElements,
} from "rxjs/operators"
import { Epic } from "../../epics"
import { addErrorNotification } from "../notifications/actions"

import * as actions from "./actions"
import { EMPTY, from, of } from "rxjs"
import { RequestCancelledError } from "../../errors"
import { IAddin, IConfiguration } from "./index"

const exportConfigurationError =
    "Something went wrong, could not export package."

export const initializeImportEpic: Epic = (action$, state$, _dependencies$) =>
    action$.pipe(
        ofType(actions.INITIALIZE),
        filter((action: actions.DeploymentAction) => {
            const {
                deployment: { initialized },
            } = state$.value
            return !initialized && action.which === "IMPORT"
        }),
        flatMap(() => [
            actions.requestInstalledConfigurations(),
            actions.setInitialized(),
        ]),
    )

export const initializeExportEpic: Epic = (action$, state$, _dependencies$) =>
    action$.pipe(
        ofType(actions.INITIALIZE),
        filter((action: actions.DeploymentAction) => {
            const {
                deployment: { initialized },
            } = state$.value
            return !initialized && action.which === "EXPORT"
        }),
        flatMap(() => [
            actions.requestConfigurations(),
            actions.setInitialized(),
        ]),
    )

export const requestConfigurationsEpic: Epic = (
    action$,
    _state$,
    { deployment: { deploymentAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_CONFIGURATIONS),
        flatMap(() => from(deploymentAPI.getConfigurations())),
        map((configurations: IConfiguration[]) =>
            actions.receiveConfigurations(configurations),
        ),
    )

export const exportConfigurationEpic: Epic = (
    action$,
    _state$,
    { deployment: { deploymentAPI } },
) =>
    action$.pipe(
        ofType(actions.EXPORT_CONFIGURATION),
        flatMap((action: actions.ConfigurationId) =>
            from(deploymentAPI.exportConfiguration(action.id)).pipe(
                ignoreElements(),
                catchError((error: Response | Error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    console.error(error)
                    return of(addErrorNotification(exportConfigurationError))
                }),
            ),
        ),
    )

export const requestInstalledConfigurationsEpic: Epic = (
    action$,
    _state$,
    { deployment: { deploymentAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_INSTALLED_CONFIGURATIONS),
        flatMap(() => from(deploymentAPI.getInstalledConfigurations())),
        flatMap((configurations: IConfiguration[]) => [
            actions.receiveConfigurations(configurations),
            ...configurations.map((configuration: IConfiguration) =>
                actions.requestPackageDiff(configuration.id),
            ),
        ]),
    )

export const requestPackageAddinDiffEpic: Epic = (
    action$,
    _state$,
    _dependencies$,
) =>
    action$.pipe(
        ofType(actions.RECEIVE_PACKAGE_DIFF),
        flatMap((action: actions.ReceivePackageDiff) => [
            actions.requestPackageAddinDiffs(
                action.packageDiff.configuration.id,
            ),
        ]),
    )

export const removeConfigurationEpic: Epic = (
    action$,
    _state$,
    { deployment: { deploymentAPI } },
) =>
    action$.pipe(
        ofType(actions.REMOVE_CONFIGURATION),
        flatMap((action: actions.ConfigurationId) =>
            from(deploymentAPI.removeConfiguration(action.id)),
        ),
        flatMap(() => [actions.requestConfigurations()]),
    )

export const getExampleEpic: Epic = (
    action$,
    _state$,
    { deployment: { deploymentAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_EXAMPLE),
        flatMap((action: actions.RequestExample) =>
            from(deploymentAPI.getExample(action.clean)),
        ),
        map((source: string) => actions.receiveSource(source)),
    )

export const getSourceEpic: Epic = (
    action$,
    _state$,
    { deployment: { deploymentAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_SOURCE),
        flatMap((action: actions.ConfigurationId) =>
            from(deploymentAPI.getSource(action.id)),
        ),
        map((source: string) => actions.receiveSource(source)),
    )

export const saveSourceEpic: Epic = (
    action$,
    _state$,
    { deployment: { deploymentAPI } },
) =>
    action$.pipe(
        ofType(actions.SAVE_SOURCE),
        flatMap((action: actions.PackageSource) =>
            from(deploymentAPI.saveSource(action.source)),
        ),
        flatMap(() => [actions.closeEditor(), actions.requestConfigurations()]),
    )

export const getPackageDiffEpic: Epic = (
    action$,
    _state$,
    { deployment: { deploymentAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_PACKAGE_DIFF),
        flatMap((action: actions.ConfigurationId) =>
            from(deploymentAPI.getPackageDiff(action.id)),
        ),
        map((packageDiff) => actions.receivePackageDiff(packageDiff)),
    )

export const requestAddinDiffsEpic: Epic = (
    action$,
    _state$,
    { deployment: { deploymentAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_PACKAGE_ADDIN_DIFFS),
        flatMap((action: actions.ConfigurationId) =>
            from(deploymentAPI.getPackageAddinDiffs(action.id)).pipe(
                map((addinDiffs: IAddin[]) => ({
                    addinDiffs,
                    id: action.id,
                })),
            ),
        ),
        map(({ id, addinDiffs }) =>
            actions.receivePackageAddinDiffs(id, addinDiffs),
        ),
    )

export const makeSnapshotEpic: Epic = (
    action$,
    _state$,
    { deployment: { deploymentAPI } },
) =>
    action$.pipe(
        ofType(actions.MAKE_SNAPSHOT),
        flatMap((action: actions.ConfigurationId) =>
            from(deploymentAPI.makeSnapshot(action.id)).pipe(
                map(() => action.id),
            ),
        ),
        map((id: string) => actions.requestPackageDiff(id)),
    )

export default combineEpics(
    initializeImportEpic,
    initializeExportEpic,
    requestConfigurationsEpic,
    requestInstalledConfigurationsEpic,
    exportConfigurationEpic,
    removeConfigurationEpic,
    getExampleEpic,
    getSourceEpic,
    saveSourceEpic,
    getPackageDiffEpic,
    requestAddinDiffsEpic,
    makeSnapshotEpic,
    requestPackageAddinDiffEpic,
)
